<template>
  <v-autocomplete
    data-testid="combined_select_exercise"
    v-model="localValue"
    :append-icon="append_icon"
    :prepend-icon="prepend_icon"
    :label="label"
    :items="exercise_options"
    :filter="exercise_filter"
    :search-input.sync="search_input"
    :menu-props="filter_menu_props"
    outlined
    @change="on_change"
  >
    <template v-if="show_filters" v-slot:prepend-item>
      <div class="combined-select-exercise-filters" @click.stop>
        <div
          v-if="filters_expanded"
          data-testid="combined_select_exercise_filters"
        >
          <v-radio-group
            v-if="show_type_filter"
            v-model="filter_exercise_type"
            row
            dense
            hide-details
            class="mt-0"
            data-testid="combined_select_exercise_filter_type"
          >
            <v-radio :label="$t('common.all')" value="all" />
            <v-radio
              v-if="allowed_types.includes('interactive')"
              value="interactive"
            >
              <template v-slot:label>
                <v-icon left small>category</v-icon>{{ $t("common.interactive") }}
              </template>
            </v-radio>
            <v-radio
              v-if="allowed_types.includes('video')"
              value="video"
            >
              <template v-slot:label>
                <v-icon left small>ondemand_video</v-icon>{{ $t("common.video") }}
              </template>
            </v-radio>
          </v-radio-group>
          <div
            class="combined-select-exercise-categories"
            data-testid="combined_select_exercise_filter_category"
          >
            <v-btn
              v-for="category in visible_category_options"
              :key="category.value"
              x-small
              outlined
              :color="filter_category === category.value ? 'primary' : undefined"
              @click="select_category(category.value)"
            >
              <v-icon left x-small>{{ category.icon }}</v-icon>
              {{ category.text }}
            </v-btn>
            <v-btn
              v-if="filter_category"
              x-small
              text
              data-testid="combined_select_exercise_clear_category"
              @click="clear_category"
            >
              {{ $t("common.clear") }}
            </v-btn>
          </div>
        </div>
        <div
          v-else
          class="combined-select-exercise-filter-summary"
          data-testid="combined_select_exercise_filter_summary"
          @click="open_filters"
        >
          <v-icon left small>mdi-filter-variant</v-icon>
          <span v-if="has_active_filters">{{ $t("common.filters") }}:</span>
          <template v-if="filter_exercise_type !== 'all'">
            <v-icon class="ml-1" small>{{ filter_type_icon }}</v-icon>
            <span>{{ $t("common." + filter_exercise_type) }}</span>
          </template>
          <template v-if="filter_category">
            <span v-if="filter_exercise_type !== 'all'" class="mx-1">·</span>
            <v-icon :class="filter_exercise_type === 'all' ? 'ml-1' : ''" small>
              {{ filter_category_icon }}
            </v-icon>
            <span>{{ get_category_display_name(filter_category) }}</span>
          </template>
        </div>
        <v-divider class="mt-2" />
      </div>
    </template>
    <template v-slot:item="data">
      <template v-if="data.item.section_header">
        <v-list-item-avatar>
          <v-icon class="combined-select-exercise-section-header">{{ data.item.icon }}</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="combined-select-exercise-section-header" v-text="data.item.text" />
        </v-list-item-content>
      </template>
      <template v-else-if="data.item.divider">
        <v-divider />
      </template>
      <template v-else-if="typeof data.item !== 'object'">
        <v-list-item-content v-text="data.item" />
      </template>
      <template v-else>
        <v-list-item-avatar>
          <v-icon>{{ data.item.icon }}</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title v-html="data.item.text" />
          <v-list-item-subtitle v-html="data.item.subtitle" />
        </v-list-item-content>
      </template>
    </template>
  </v-autocomplete>
</template>

<script>
import { get_category_icon, get_category_name } from "../lib/category";
import { mapState } from "vuex";

export default {
  components: {},
  props: {
    value: {
      type: String,
      required: true,
    },
    prepend_icon: {
      type: String,
      default: "",
    },
    append_icon: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      required: true,
    },
    allowed_types: {
      type: Array,
      default: () => ["interactive", "video"],
    },
    show_filters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filter_exercise_type: "all",
      filter_category: null,
      search_input: "",
      filters_forced_open: false,
    };
  },
  computed: {
    ...mapState([
      "current_user",
      "current_clinic",
      "current_clinic_selections",
      "current_clinic_prefs",
      "exercises",
      "video_exercises",
    ]),
    localValue: {
      get() {
        return this.value;
      },
      set(localValue) {
        this.$emit("input", localValue);
      },
    },
    custom_categories() {
      return this.current_clinic_prefs?.custom_categories || [];
    },
    show_type_filter() {
      return this.allowed_types.length > 1;
    },
    filter_menu_props() {
      if (!this.show_filters) {
        return undefined;
      }
      return { minWidth: 900, maxHeight: 500, closeOnContentClick: false };
    },
    selected_option_text() {
      if (!this.localValue) {
        return "";
      }
      if (this.exercises && this.exercises[this.localValue]) {
        return this.exercise_title(this.exercises[this.localValue]).trim();
      }
      if (this.video_exercises && this.video_exercises[this.localValue]) {
        return this.exercise_title(this.video_exercises[this.localValue]).trim();
      }
      return "";
    },
    is_searching() {
      const query = (this.search_input || "").trim();
      if (!query) {
        return false;
      }
      return query !== this.selected_option_text;
    },
    filters_expanded() {
      return !this.is_searching || this.filters_forced_open;
    },
    has_active_filters() {
      return this.filter_exercise_type !== "all" || !!this.filter_category;
    },
    filter_type_icon() {
      return this.filter_exercise_type === "interactive"
        ? "category"
        : "ondemand_video";
    },
    filter_category_icon() {
      if (!this.filter_category) {
        return "";
      }
      return get_category_icon(this.filter_category, this.custom_categories);
    },
    video_selections() {
      let selections = {};

      if (!this.current_clinic_selections || !this.allowed_types.includes("video")) {
        return selections;
      }

      for (let builtin_select of this.current_clinic_selections.get_by_type(
        "video/builtin"
      )) {
        selections[builtin_select.entity_id] = builtin_select.selected;
      }
      for (let video_select of this.current_clinic_selections.get_by_type(
        "video_exercise"
      )) {
        selections[video_select.entity_id] = video_select.selected;
      }

      return selections;
    },
    type_filtered_groups() {
      let grouped = [];

      const sources = [];
      if (
        this.allowed_types.includes("interactive") &&
        this.exercises &&
        (this.filter_exercise_type === "all" ||
          this.filter_exercise_type === "interactive")
      ) {
        sources.push({ exercises: this.exercises, exercise_type: "interactive" });
      }
      if (
        this.allowed_types.includes("video") &&
        this.video_exercises &&
        (this.filter_exercise_type === "all" ||
          this.filter_exercise_type === "video")
      ) {
        sources.push({ exercises: this.video_exercises, exercise_type: "video" });
      }

      for (let source of sources) {
        for (let exercise_id in source.exercises) {
          let exercise = source.exercises[exercise_id];
          if (
            exercise.demo_only ||
            (exercise.canary &&
              (!this.current_clinic || !this.current_clinic.has_tag("canary")) &&
              !this.current_user.is_admin) ||
            (exercise.deprecated && this.localValue != exercise_id) ||
            (source.exercise_type == "video" &&
              this.video_selections[exercise_id] === false)
          ) {
            continue;
          }

          let group_name = exercise.group && exercise.group[0];
          if (!group_name) {
            group_name = "Uncategorized";
          }

          let group_exists = false;
          let group;
          for (let i in grouped) {
            if (grouped[i].group == group_name) {
              group_exists = true;
              group = grouped[i];
            }
          }

          if (!group_exists) {
            group = {
              group: group_name,
              exercises: [],
              icon: get_category_icon(group_name, this.custom_categories),
            };
          }

          group.exercises.push({
            ...exercise,
            exercise_type: source.exercise_type,
          });
          if (!group_exists) {
            grouped.push(group);
          }
        }
      }

      grouped.sort((a, b) => {
        if (a.group == "Uncategorized") {
          return 1;
        }
        if (b.group == "Uncategorized") {
          return -1;
        }
        return this.get_category_display_name(a.group).localeCompare(
          this.get_category_display_name(b.group)
        );
      });

      for (let g in grouped) {
        grouped[g].exercises.sort((a, b) =>
          this.exercise_title(a).localeCompare(this.exercise_title(b))
        );
      }

      return grouped;
    },
    category_options() {
      return this.type_filtered_groups.map((group) => ({
        value: group.group,
        text: this.get_category_display_name(group.group),
        icon: group.icon,
      }));
    },
    visible_category_options() {
      if (!this.filter_category) {
        return this.category_options;
      }
      return this.category_options.filter(
        (category) => category.value === this.filter_category
      );
    },
    exercise_options() {
      let options = [];
      let grouped = this.type_filtered_groups;

      if (
        this.filter_category &&
        grouped.some((group) => group.group === this.filter_category)
      ) {
        grouped = grouped.filter((group) => group.group === this.filter_category);
      }

      for (let group_g in grouped) {
        let group = grouped[group_g];
        if (group_g != 0) {
          options.push({ divider: true });
        }
        options.push({
          section_header: true,
          text: this.get_category_display_name(group.group),
          icon: group.icon,
          disabled: true,
        });

        for (let e_id in group.exercises) {
          let exercise = group.exercises[e_id];
          let desc = this.exercise_description(exercise);
          desc = desc.split(".")[0];

          options.push({
            value: exercise.id,
            text: this.exercise_title(exercise),
            subtitle: desc,
            icon:
              exercise.exercise_type == "interactive"
                ? "category"
                : "ondemand_video",
            exercise_type: exercise.exercise_type,
            group: group.group,
          });
        }
      }

      return options;
    },
  },
  watch: {
    is_searching(searching, was_searching) {
      if (searching && !was_searching) {
        this.filters_forced_open = false;
      }
      if (!searching) {
        this.filters_forced_open = false;
      }
    },
    category_options: {
      immediate: true,
      handler(options) {
        if (
          this.filter_category &&
          !options.some((option) => option.value === this.filter_category)
        ) {
          this.filter_category = null;
        }
      },
    },
  },
  methods: {
    on_change(value) {
      if (!value) {
        this.$emit("change", null);
        return;
      }
      const selected = this.exercise_options.find(
        (item) => item.value === value && item.exercise_type
      );
      this.$emit("change", selected || null);
    },
    select_category(category) {
      this.filter_category = category;
    },
    clear_category() {
      this.filter_category = null;
    },
    open_filters() {
      this.filters_forced_open = true;
    },
    get_category_display_name(category_id) {
      if (
        category_id &&
        category_id.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        )
      ) {
        return get_category_name(category_id, this.custom_categories);
      }
      return this.$t("group." + (category_id || "Uncategorized"));
    },
    exercise_filter(item, queryText, _displayTitle) {
      if (item.section_header || item.header || item.divider) {
        return false;
      }
      let filter = queryText.trim().toLowerCase();
      let title = (item.text || "").trim().toLowerCase();
      let title_stripped = title.replace(/\./g, "");
      let subtitle = (item.subtitle || "").trim().toLowerCase();

      if (
        title.includes(filter) ||
        title_stripped.includes(filter) ||
        subtitle.includes(filter)
      ) {
        return true;
      }

      return false;
    },
    exercise_title(exercise) {
      if (!exercise.title) {
        return "";
      }

      if (typeof exercise.title == "string") {
        return exercise.title;
      }

      let locale = this.$i18n.lang();
      if (exercise.title[locale]) {
        return exercise.title[locale];
      } else {
        return exercise.title.en;
      }
    },
    exercise_description(exercise) {
      if (!exercise.description) {
        return "";
      }

      if (typeof exercise.description == "string") {
        return exercise.description;
      }

      let locale = this.$i18n.lang();
      if (exercise.description[locale]) {
        return exercise.description[locale];
      } else {
        return exercise.description.en;
      }
    },
  },
};
</script>

<style>
.combined-select-exercise-filters {
  padding: 8px 16px 4px;
}

.combined-select-exercise-categories {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 900px;
  margin-top: 8px;
}

.combined-select-exercise-categories .v-btn {
  margin: 2px 4px 2px 0;
}

.combined-select-exercise-categories .v-btn.v-btn--outlined:not(.primary--text) {
  border-color: #9e9e9e;
}

.combined-select-exercise-categories .v-btn.v-btn--outlined:not(.primary--text) .v-icon {
  color: #9e9e9e;
}

.theme--light .combined-select-exercise-section-header {
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .combined-select-exercise-section-header {
  color: rgba(255, 255, 255, 0.87);
}

.combined-select-exercise-filter-summary {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  opacity: 0.8;
  padding: 4px 0;
}
</style>
